import moment from 'moment-timezone';
import { ClickStatApiModel, PostingStatApiModel, ReleaseStatsApiModel } from '../../../api/wire/models/release-stats-api.model';
import { trimLongStringWithEllipsis } from '../../../Core/utils';

export class ReleaseStatsModel {
    newswireReleaseId: number;
    totalPotentialVisibility: number;
    totalTraffic: number;
    totalViews: number;
    lastUpdated: number;
    postingStats: PostingStatModel[];
    sourceClickStats: ClickStatModel[];
    sourceTotalClicks: number;
    topSourceClicks: ClickStatModel[];
    targetClickStats: ClickStatModel[];
    targetTotalClicks: number;
    topTargetClicks: ClickStatModel[];
    totalNumberOfPostings: number;
    totalLinkClicks: number;

    constructor(apiModel?: ReleaseStatsApiModel) {
        if (apiModel) {
            this.newswireReleaseId = apiModel.newswireReleaseId;
            this.totalPotentialVisibility = apiModel.totalPotentialVisibility;
            this.totalTraffic = apiModel.totalTraffic;
            this.totalViews = apiModel.totalViews;
            this.lastUpdated = apiModel.lastUpdated;

            this.totalNumberOfPostings = 0;
            if (apiModel.postingStats) {
                this.totalNumberOfPostings = apiModel.postingStats.length;
                this.postingStats = apiModel.postingStats.map(postingStat => {
                    return new PostingStatModel(postingStat);
                });
            }

            this.sourceTotalClicks = 0;
            if (apiModel.sourceClickStats?.length > 0) {
                this.sourceClickStats = apiModel.sourceClickStats.map(clickStat => {
                    this.sourceTotalClicks += clickStat.clicks;
                    return new ClickStatModel(clickStat);
                }).map(clickStat => {
                    clickStat.clickRate = clickStat.clicks / this.sourceTotalClicks;
                    return clickStat;
                });
                this.topSourceClicks = this.sourceClickStats.sort((a, b) => b.clicks - a.clicks)
                    .slice(0, Math.min(3, this.sourceClickStats.length));
            } else {
                this.sourceClickStats = [];
                this.topSourceClicks = [];
            }

            this.targetTotalClicks = 0;
            if (apiModel.targetClickStats?.length > 0) {
                this.targetClickStats = apiModel.targetClickStats.map(clickStat => {
                    this.targetTotalClicks += clickStat.clicks;
                    return new ClickStatModel(clickStat);
                }).map(clickStat => {
                    clickStat.clickRate = clickStat.clicks / this.targetTotalClicks;
                    return clickStat;
                });
                this.topTargetClicks = this.targetClickStats.sort((a, b) => b.clicks - a.clicks)
                    .slice(0, Math.min(3, this.targetClickStats.length));
            } else {
                this.targetClickStats = [];
                this.topTargetClicks = [];
            }

            this.totalLinkClicks = this.sourceTotalClicks + this.targetTotalClicks;
        }
    }

    toApiModel(): ReleaseStatsApiModel {
        return {
            newswireReleaseId: this.newswireReleaseId,
            totalPotentialVisibility: this.totalPotentialVisibility,
            totalTraffic: this.totalTraffic,
            totalViews: this.totalViews,
            lastUpdated: this.lastUpdated,
            postingStats: this.postingStats ? this.postingStats.map(postingStat => postingStat.toApiModel()) : [],
            sourceClickStats: this.sourceClickStats ? this.sourceClickStats.map(clickStat => clickStat.toApiModel()) : [],
            targetClickStats: this.targetClickStats ? this.targetClickStats.map(clickStat => clickStat.toApiModel()) : [],
        };
    }
}

export class PostingStatModel {
    siteName: string;
    siteNameDisplayString: string;
    siteUrl: string;
    siteUrlDisplayString: string;
    releaseUrl: string;
    releaseUrlDisplayString: string;
    nqFirstSeen: number;
    nqFirstSeenDateString: string;
    potentialVisibility: number;
    totalTraffic: number;
    thumbUrl: string;
    mozDaScore: number;
    alexaGlobalRank: number;
    alexaGlobalChange: number;

    constructor(apiModel: PostingStatApiModel) {
        if (apiModel) {
            this.siteName = apiModel.siteName;
            this.siteNameDisplayString = trimLongStringWithEllipsis(apiModel.siteName, 18);
            this.siteUrl = apiModel.siteUrl;
            this.siteUrlDisplayString = trimLongStringWithEllipsis(apiModel.siteUrl, 35);
            this.releaseUrl = apiModel.releaseUrl;
            this.releaseUrlDisplayString = trimLongStringWithEllipsis(apiModel.releaseUrl, 35);
            this.nqFirstSeen = apiModel.nqFirstSeen;
            this.nqFirstSeenDateString = moment(this.nqFirstSeen).format('MMM DD, YYYY hh:mm A z');
            this.potentialVisibility = apiModel.potentialVisibility;
            this.totalTraffic = apiModel.totalTraffic;
            this.thumbUrl = apiModel.thumbUrl;
            this.mozDaScore = apiModel.mozDaScore;
            this.alexaGlobalRank = apiModel.alexaGlobalRank;
            this.alexaGlobalChange = apiModel.alexaGlobalChange;
        }
    }

    toApiModel(): PostingStatApiModel {
        return {
            siteName: this.siteName,
            siteUrl:  this.siteUrl,
            releaseUrl: this.releaseUrl,
            nqFirstSeen: this.nqFirstSeen,
            potentialVisibility: this.potentialVisibility,
            totalTraffic: this.totalTraffic,
            thumbUrl: this.thumbUrl,
            mozDaScore: this.mozDaScore,
            alexaGlobalRank: this.alexaGlobalRank,
            alexaGlobalChange: this.alexaGlobalChange
        };
    }
}

export class ClickStatModel {
    url: string;
    urlDisplayString: string;
    clicks: number;
    clickRate: number;

    constructor(apiModel: ClickStatApiModel) {
        if (apiModel) {
            this.url = apiModel.url;
            this.urlDisplayString = trimLongStringWithEllipsis(apiModel.url, 45);
            this.clicks = apiModel.clicks;
        }
    }

    toApiModel(): ClickStatApiModel {
        return {
            url: this.url,
            clicks: this.clicks
        };
    }
}
